import clsx from 'clsx';
import type {
  AuthenticatedUser,
  Comment,
} from '@frameio/graphql-client/generated';
import { Avatar, Text } from '@frameio/vapor';
import type { ThemeColorsBackground } from '@frameio/vapor-theme';
import { GLPYHS } from '../../../../constants';
import { HighlightText } from '../../HighlightText';
import styles from './CommentFieldMatch.module.css';

type CommentFieldMatchProps = {
  comment: Comment;
  snippet?: string | null;
  terms?: string[] | null;
  variant?: 'block' | 'inline';
};

export const CommentFieldMatch = ({
  comment,
  snippet,
  terms,
  variant = 'block',
}: CommentFieldMatchProps) => {
  const commentText = snippet || comment.text || '';
  const hasHighlights = Boolean(terms?.length);
  const src =
    (comment?.owner as AuthenticatedUser)?.profileImage?.image64 || '';

  return (
    <div className={styles.container}>
      {comment.owner && (
        <div className={styles.owner}>
          <Avatar
            alt={comment.owner.name ?? ''}
            background={comment.owner.avatarColor as ThemeColorsBackground}
            size={16}
            src={src}
            style={{ flexShrink: 0 }}
          />
          <div className={styles.ownerName}>
            <Text
              color="secondary"
              enableTooltip={true}
              fontWeight="semiBold"
              truncate={true}
              variant="caption"
            >
              {comment.owner.name}
            </Text>
          </div>
        </div>
      )}
      <div
        className={clsx(styles.quote, variant === 'inline' && styles.inline)}
      >
        <Text
          color="tertiary"
          fontWeight="regular"
          style={{ flexShrink: 0 }}
          variant="caption"
        >
          {GLPYHS.openQuotes}
        </Text>
        <>
          {hasHighlights ? (
            <HighlightText
              className={styles.quoteText}
              color="tertiary"
              fontWeight="regular"
              highlights={terms ?? ''}
              text={commentText ?? ''}
              truncate={true}
              variant="caption"
            />
          ) : (
            <Text
              className={styles.quoteText}
              color="tertiary"
              enableTooltip={true}
              fontWeight="regular"
              truncate={true}
              variant="caption"
            >
              {commentText}
            </Text>
          )}
        </>
        <Text
          color="tertiary"
          fontWeight="regular"
          style={{ flexShrink: 0 }}
          variant="caption"
        >
          {GLPYHS.closeQuotes}
        </Text>
      </div>
    </div>
  );
};
